import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getManufacturerSelectList } from '../../clients/manufacturer_client';
import globalManufacturerSelectCache from '../../config_and_helpers/manufacturer_cache';
import DropdownMultiselect from '../Generic/DropdownMultiselect';
import {
  checkSupplierEmail,
  checkSupplierName,
  checkSupplierPhone,
  createSupplier,
  updateSupplier,
  getSupplierDetailsLazy,
} from '../../clients/supplier_client';
import { Formik } from 'formik';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import * as yup from 'yup';
import {
  emailRegex,
  PHONE_PREFIX_CHOICES,
} from '../../config_and_helpers/config';

import './SupplierComponentStyle.css';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';
import { PencilFill } from 'react-bootstrap-icons';
import {
  showToast,
  updateObjectInObjectArray,
} from '../../config_and_helpers/helpers';
import SecondaryEmailComponent from './SecondaryEmailComponent';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import CustomSupplierContextHook from '../../pages/Supplier/CustomSupplierContextHook';
import {
  SupplierCache,
  invalidateSupplierSelectCache,
} from '../../config_and_helpers/supplier_cache';

// Ref to track if we've requested full data for this supplier (module-level to persist across renders)
const requestedFullDataRef = new Map();

// Yup schema for the supplier Formik form
const formSchema = yup.object().shape({
  name: yup.string().required('Supplier name is required'),
  websiteUrl: yup.string().url('Enter a valid URL'),
  email: yup.string().matches(emailRegex, 'Enter a valid email'),
  phonePrefix: yup.string(),
  phoneSuffix: yup.string().matches(/^\d*$/, 'Phone number must be numeric'),
  secondaryEmails: yup
    .array()
    .of(yup.string().matches(emailRegex, 'Enter a valid email')),
  innerSupplierId: yup
    .string()
    .max(100, 'Inner supplier ID must be 100 characters or less'),
});

/**
 * Represents a SupplierModal component.
 *
 * @param {Object} props - The properties of the SupplierModal component.
 * @param {function} props.onSuccessfulSubmit - The function to be called on successful submit of the form.
 * @param {Object} props.supplierObj - The supplier object to be edited. If provided, the form will be pre-filled with the supplier's information.
 * @param {function} props.clearSearchValue - The function to clear the search value in the parent component.
 *
 * @returns {element} - The SupplierModal component.
 */
const SupplierModal = ({
  onSuccessfulSubmit,
  supplierObj,
  clearSearchValue,
  onEditButtonHover,
  isEditButtonLoading,
  onSupplierUpdate,
}) => {
  const { setSuppliers } = CustomSupplierContextHook();
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const debounceTimeoutRef = useRef(null);
  const debounceEmailTimeoutRef = useRef(null);
  const debouncePhoneTimeoutRef = useRef(null);
  const postCloseActionRef = useRef(null);
  const [relatedManufacturers, setRelatedManufacturers] = useState(
    supplierObj && supplierObj.manufacturers
      ? supplierObj.manufacturers.map(item => ({
          // Map manufacturers to id/label pairs
          value: item.id,
          label: item.name,
        }))
      : [] // Initialize with empty array if supplierObj is not provided
  );
  const [manufacturerList, setManufacturerList] = useState([]); // State to hold list of manufacturers
  const [name, setName] = useState(supplierObj?.name || ''); // State to hold name, default to provided supplier's name
  // State to hold the boolean whether supplier name is unique or not. Initialized as true
  const [isSupplierNameUnique, setIsSupplierNameUnique] = useState(true);
  // State to indicate whether the system is still checking for unique supplier name. Initialized as false
  const [isCheckingSupplierName, setIsCheckingSupplierName] = useState(false);
  // More state variables for phone prefix, suffix, supplier's unique phone
  // The rest is the same pattern as name
  // State variables for email and submitting status
  const [phonePrefix, setPhonePrefix] = useState(
    supplierObj?.phone_prefix || ''
  );
  const [phoneSuffix, setPhoneSuffix] = useState(
    supplierObj?.phone_suffix || ''
  );
  const [isSupplierPhoneUnique, setIsSupplierPhoneUnique] = useState(true);
  const [isCheckingSupplierPhone, setIsCheckingSupplierPhone] = useState(false);
  const [supplierEmail, setSupplierEmail] = useState(supplierObj?.email || '');
  const [innerSupplierId, setInnerSupplierId] = useState(
    supplierObj?.inner_supplier_id || ''
  );
  const [secondaryEmails, setSecondaryEmails] = useState(
    supplierObj?.secondary_emails
      ? supplierObj.secondary_emails.map(secondaryEmail => secondaryEmail.email)
      : []
  );

  const [secondaryEmailsValid, setSecondaryEmailsValid] = useState(true);
  const [isSupplierEmailUnique, setIsSupplierEmailUnique] = useState(true);
  const [isCheckingSupplierEmail, setIsCheckingSupplierEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false); // State for controlling the modal visibility
  const [isLoadingManufacturers, setIsLoadingManufacturers] = useState(false); // State for loading manufacturers
  const prevShowModalRef = useRef(false); // Track previous showModal value to detect when it opens

  // Function to fetch manufacturer select list on hover
  const fetchManufacturerSelectList = useCallback(() => {
    // Use global cache if available
    if (globalManufacturerSelectCache.data) {
      setManufacturerList(globalManufacturerSelectCache.data);
      return;
    }

    // If already fetching, wait for the promise
    if (
      globalManufacturerSelectCache.isFetching &&
      globalManufacturerSelectCache.promise
    ) {
      setIsLoadingManufacturers(true);
      globalManufacturerSelectCache.promise.then(() => {
        if (globalManufacturerSelectCache.data) {
          setManufacturerList(globalManufacturerSelectCache.data);
        }
        setIsLoadingManufacturers(false);
      });
      return;
    }

    // Fetch and cache the data
    setIsLoadingManufacturers(true);
    globalManufacturerSelectCache.isFetching = true;
    globalManufacturerSelectCache.promise = getManufacturerSelectList(
      token,
      data => {
        globalManufacturerSelectCache.data = data;
        globalManufacturerSelectCache.isFetching = false;
        setManufacturerList(data);
        setIsLoadingManufacturers(false);
      }
    );

    globalManufacturerSelectCache.promise.then(() => {
      globalManufacturerSelectCache.isFetching = false;
    });
  }, [token]);

  // Fetch manufacturer select list when modal is opened (fallback)
  useEffect(() => {
    if (showModal) {
      fetchManufacturerSelectList();
    }
  }, [showModal, fetchManufacturerSelectList]);

  // ===== LAZY LOADING: Fetch full supplier data when modal opens =====
  // This ensures secondary_emails are available when editing a supplier
  useEffect(() => {
    if (
      showModal &&
      supplierObj &&
      !supplierObj.secondary_emails &&
      supplierObj.id
    ) {
      const supplierId = supplierObj.id;

      // Prevent duplicate fetches for the same supplier
      if (!requestedFullDataRef.has(supplierId)) {
        requestedFullDataRef.set(supplierId, true);

        getSupplierDetailsLazy(token, supplierId)
          .then(fullData => {
            // Update secondaryEmails state from fetched data
            if (
              fullData?.secondary_emails &&
              fullData.secondary_emails.length > 0
            ) {
              const emails = fullData.secondary_emails.map(
                secondaryEmail => secondaryEmail.email
              );
              setSecondaryEmails(emails);
            }

            // Update inner_supplier_id state from fetched data
            if (fullData?.inner_supplier_id !== undefined) {
              setInnerSupplierId(fullData.inner_supplier_id || '');
            }

            // Update parent component cache
            if (onSupplierUpdate) {
              onSupplierUpdate(supplierId, fullData);
            }

            // Update suppliers list with full data
            setSuppliers(prevSuppliers =>
              updateObjectInObjectArray(prevSuppliers, supplierId, fullData)
            );

            requestedFullDataRef.delete(supplierId);
          })
          .catch(error => {
            requestedFullDataRef.delete(supplierId);
          });
      }
    }
  }, [showModal, supplierObj, token, onSupplierUpdate, setSuppliers]);

  useEffect(() => {
    return () => {
      // Clean up debounce timeouts
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (debounceEmailTimeoutRef.current) {
        clearTimeout(debounceEmailTimeoutRef.current);
      }
      if (debouncePhoneTimeoutRef.current) {
        clearTimeout(debouncePhoneTimeoutRef.current);
      }
    };
  }, []);

  // Validators to check uniqueness of name, email and phone number
  const supplierNameUniqueValidator = {
    id: 'unique',
    text: 'Name already taken.',
    validate: () => (isCheckingSupplierName ? true : isSupplierNameUnique),
  };

  // More validators for email and phone number. Same pattern as name

  const supplierEmailUniqueValidator = {
    id: 'unique',
    text: 'Email address already taken.',
    validate: () => (isCheckingSupplierEmail ? true : isSupplierEmailUnique),
  };

  const supplierPhoneUniqueValidator = {
    id: 'unique',
    text: 'Phone number already taken.',
    validate: () => (isCheckingSupplierPhone ? true : isSupplierPhoneUnique),
  };

  // Asynchronous function to validate supplier name
  const validateSupplierName = useCallback(
    async value => {
      const response = await checkSupplierName(token, value);
      setIsCheckingSupplierName(false);
      setIsSupplierNameUnique(response);
    },
    [token]
  );

  // More validators for email and phone number. Same pattern as name
  const validateSupplierEmail = useCallback(
    async value => {
      const response = await checkSupplierEmail(token, value);
      setIsCheckingSupplierEmail(false);
      setIsSupplierEmailUnique(response);
    },
    [token]
  );

  const validateSupplierPhone = useCallback(
    async (prefix, suffix) => {
      const response = await checkSupplierPhone(token, prefix, suffix);
      setIsCheckingSupplierPhone(false);
      setIsSupplierPhoneUnique(response);
    },
    [token]
  );

  // Debounced version of validator function to prevent excessive API calls when fast typing
  // Using debounce from lodash for checking Supplier name, email and phone
  // 1500 milliseconds set as the wait time. Adjust according to your needs
  const debouncedCheckSupplierName = useCallback(
    value => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        validateSupplierName(value);
      }, 1500);
    },
    [validateSupplierName]
  );

  const debouncedCheckSupplierEmail = useCallback(
    value => {
      if (debounceEmailTimeoutRef.current) {
        clearTimeout(debounceEmailTimeoutRef.current);
      }
      debounceEmailTimeoutRef.current = setTimeout(() => {
        validateSupplierEmail(value);
      }, 1500);
    },
    [validateSupplierEmail]
  );

  const debouncedCheckSupplierPhone = useCallback(
    (prefix, suffix) => {
      if (debouncePhoneTimeoutRef.current) {
        clearTimeout(debouncePhoneTimeoutRef.current);
      }
      debouncePhoneTimeoutRef.current = setTimeout(() => {
        validateSupplierPhone(prefix, suffix);
      }, 1500);
    },
    [validateSupplierPhone]
  );

  useEffect(() => {
    if (name && name !== supplierObj?.name) {
      debouncedCheckSupplierName(name);
    } else {
      setIsCheckingSupplierName(false);
    }
  }, [name, debouncedCheckSupplierName, supplierObj?.name]);

  // Similar useEffect hooks for supplier email and phone
  useEffect(() => {
    if (supplierEmail && supplierEmail !== supplierObj?.email) {
      debouncedCheckSupplierEmail(supplierEmail);
    } else {
      setIsCheckingSupplierEmail(false);
    }
  }, [supplierEmail, debouncedCheckSupplierEmail, supplierObj?.email]);

  useEffect(() => {
    if (
      phonePrefix &&
      phoneSuffix &&
      phoneSuffix.length === 7 &&
      (phonePrefix !== supplierObj?.phone_prefix ||
        phoneSuffix !== supplierObj?.phone_suffix)
    ) {
      debouncedCheckSupplierPhone(phonePrefix, phoneSuffix);
    } else {
      setIsSupplierPhoneUnique(true);
      setIsCheckingSupplierPhone(false);
    }
  }, [
    phonePrefix,
    phoneSuffix,
    debouncedCheckSupplierPhone,
    supplierObj?.phone_prefix,
    supplierObj?.phone_suffix,
  ]);

  // Update relatedManufacturers when supplierObj changes (for lazy loading)
  useEffect(() => {
    if (supplierObj && supplierObj.manufacturers) {
      setRelatedManufacturers(
        supplierObj.manufacturers.map(item => ({
          value: item.id,
          label: item.name,
        }))
      );
    } else if (supplierObj && !supplierObj.manufacturers) {
      setRelatedManufacturers([]);
    }
  }, [supplierObj]);

  // Update inner_supplier_id when supplierObj changes
  useEffect(() => {
    if (supplierObj?.inner_supplier_id !== undefined) {
      setInnerSupplierId(supplierObj.inner_supplier_id || '');
    }
  }, [supplierObj]);

  // ===== POPULATE SECONDARY EMAILS: Update form when supplierObj changes =====
  // This populates the secondary emails form fields when editing a supplier
  useEffect(() => {
    prevShowModalRef.current = showModal;

    // Only process when modal is open and supplierObj exists
    if (!showModal || !supplierObj) {
      return;
    }

    // Case 1: Secondary emails exist - populate form
    if (
      supplierObj.secondary_emails &&
      Array.isArray(supplierObj.secondary_emails) &&
      supplierObj.secondary_emails.length > 0
    ) {
      const emails = supplierObj.secondary_emails.map(
        secondaryEmail => secondaryEmail.email
      );
      setSecondaryEmails(emails);
    }
    // Case 2: Secondary emails are undefined/null - might be loading, don't clear if fetching
    else if (
      supplierObj.secondary_emails === undefined ||
      supplierObj.secondary_emails === null
    ) {
      if (!requestedFullDataRef.has(supplierObj.id)) {
        setSecondaryEmails([]);
      }
    }
    // Case 3: Explicitly empty array - no secondary emails
    else if (
      Array.isArray(supplierObj.secondary_emails) &&
      supplierObj.secondary_emails.length === 0
    ) {
      setSecondaryEmails([]);
    }
  }, [supplierObj, showModal]);

  const handleClose = () => {
    setShowModal(false); // Function to close the modal
    setSupplierEmail('');
    setName('');
    setPhonePrefix('');
    setPhoneSuffix('');
    setInnerSupplierId('');
    setSecondaryEmails([]);
  };

  const resetModal = () => {
    setRelatedManufacturers([]); // Function to reset the modal state
  };

  const handleShow = () => setShowModal(true); // Function to open the modal
  const handleModalExited = () => {
    if (postCloseActionRef.current) {
      postCloseActionRef.current();
      postCloseActionRef.current = null;
    }
  };

  // Function to handle the addition of a new item into the secondaryEmails Array
  const addSecondaryEmail = e => {
    e.preventDefault();
    setSecondaryEmails([...secondaryEmails, '']);
  };

  // Function to handle the change of a given secondary email value
  const updateSecondaryEmail = (e, index, value) => {
    e.preventDefault();
    const newSecondaryEmails = [...secondaryEmails];
    newSecondaryEmails[index] = value;
    setSecondaryEmails(newSecondaryEmails);
  };

  // Function to handle the deletion of a secondary email
  const removeSecondaryEmail = (e, index) => {
    e.preventDefault();
    const newSecondaryEmails = [...secondaryEmails];
    newSecondaryEmails.splice(index, 1);
    setSecondaryEmails(newSecondaryEmails);
  };

  // Function to handle submitting the supplier form
  function handleSubmit(values) {
    // Set submitting immediately - button is fixed size so no layout shift
    setIsSubmitting(true);

    // Assemble the supplier data from the form values and state
    const supplierData = {
      name: values.name,
      website: values.websiteUrl,
      email: values.email,
      phone_prefix: values.phonePrefix,
      phone_suffix: values.phoneSuffix,
      manufacturers: relatedManufacturers // Convert array of selected manufacturers to comma separated string
        .map(manufacturer => manufacturer.value)
        .join(','),
    };

    // Add inner_supplier_id if provided (can be empty string for clearing)
    if (values.innerSupplierId !== undefined) {
      supplierData.inner_supplier_id = values.innerSupplierId || null;
    }

    supplierData.include_in_finance_digest = Boolean(
      values.includeInFinanceDigest
    );

    // check if the 'supplierObj' not exist
    if (!supplierObj) {
      // check if there are secondary email addresses
      if (secondaryEmails.length > 0) {
        // assign the secondary email addresses to the 'supplierData'
        supplierData.secondary_emails = secondaryEmails;
      }
    } else {
      // 'supplierObj' does exist
      // check if the 'supplierObj' has no secondary email addresses and there's ones provided
      if (
        supplierObj.secondary_emails.length === 0 &&
        secondaryEmails.length > 0
      ) {
        // assign the new secondary emails to the 'supplierData'
        supplierData.secondary_emails = secondaryEmails;
      }
      // check if the 'supplierObj' has secondary email addresses
      if (supplierObj.secondary_emails.length > 0) {
        // filter out the email objects that are not included in the provided secondary emails and get their IDs
        const idsOfEmailsNotIncluded = supplierObj.secondary_emails
          .filter(emailObj => !secondaryEmails.includes(emailObj.email))
          .map(emailObj => emailObj.id);
        // filter out the secondary emails that are not already in the 'supplierObj'
        const secondaryEmailsToAdd = secondaryEmails.filter(
          email =>
            !supplierObj.secondary_emails.some(
              emailObj => emailObj.email === email
            )
        );

        // check if there are any email ids that were not included
        if (idsOfEmailsNotIncluded.length > 0) {
          // add these ids to a new array in the 'supplierData' to mark them for deletion
          supplierData.secondary_emails_to_delete = idsOfEmailsNotIncluded;
        }
        // check if there are new secondary emails to be added
        if (secondaryEmailsToAdd.length > 0) {
          // add these new emails to the 'supplierData'
          supplierData.secondary_emails = secondaryEmailsToAdd;
        }
      }
    }

    // Depending on whether a supplierObj was passed in (indicating an edit), either call updateSupplier or createSupplier
    // Don't pass onSuccessfulSubmit to updateSupplier - it causes state updates while modal is open, triggering ResizeObserver
    const supplierPromise = supplierObj
      ? updateSupplier(token, supplierObj.id, supplierData, null)
      : createSupplier(token, supplierData);

    supplierPromise.then(response => {
      if (response && response.success) {
        // Schedule actions to run after the modal fully closes to prevent ResizeObserver errors
        postCloseActionRef.current = async () => {
          if (supplierObj) {
            // ===== UPDATE SUPPLIER FLOW =====
            // Clear cache before fetching to ensure fresh data
            SupplierCache.remove(supplierObj.id);

            // Determine if we need to fetch full data or can use response data
            const responseHasSecondaryEmails =
              response.updatedSupplier?.secondary_emails !== undefined;
            let fullSupplierData = response.updatedSupplier;

            // Only fetch if secondary_emails are missing from response
            if (!responseHasSecondaryEmails) {
              try {
                fullSupplierData = await getSupplierDetailsLazy(
                  token,
                  supplierObj.id
                );
              } catch (error) {
                // Fallback to response data if fetch fails
                fullSupplierData = response.updatedSupplier;
              }
            }

            // Update suppliers list with complete supplier data
            setSuppliers(prevSuppliers =>
              updateObjectInObjectArray(
                prevSuppliers,
                supplierObj.id,
                fullSupplierData
              )
            );

            // Update cache with fresh data
            SupplierCache.set(supplierObj.id, fullSupplierData);

            // Invalidate supplier select list cache to force refresh on next fetch
            invalidateSupplierSelectCache();

            // Notify parent component of update
            if (onSupplierUpdate) {
              onSupplierUpdate(supplierObj.id, fullSupplierData);
            }
          } else {
            // ===== CREATE SUPPLIER FLOW =====
            // Invalidate supplier select list cache to force refresh on next fetch
            invalidateSupplierSelectCache();

            if (onSuccessfulSubmit) {
              onSuccessfulSubmit();
            }
            if (clearSearchValue) {
              clearSearchValue();
            }
            resetModal();
          }

          // Show success toast and reset submitting state
          response.toast();
          setIsSubmitting(false);
        };
        handleClose();
      } else {
        // If the API call was not successful
        showToast(
          // Show an error toast
          'An unexpected error occurred. Please try again in a little while.',
          'error',
          'top-right',
          3000
        );
        setIsSubmitting(false); // Set submitting status to false
      }
    });
  }

  if (isViewer) {
    return null;
  }

  return (
    <>
      {/* Button for opening the modal */}
      <Button
        variant={supplierObj ? 'outline-success' : 'success'}
        onClick={handleShow}
        onMouseEnter={() => {
          fetchManufacturerSelectList();
          if (onEditButtonHover) {
            onEditButtonHover();
          }
        }}
      >
        {/* Change button content depending on whether it's in edit mode or create mode */}
        {supplierObj ? <PencilFill /> : 'Create Supplier'}
      </Button>

      {/* Modal component */}
      <Modal
        show={showModal}
        onHide={handleClose}
        backdrop="static"
        onExited={handleModalExited}
        dialogClassName="procure-modal-glass procure-modal-glass-form"
      >
        <Modal.Header closeButton className="procure-modal__header">
          {/* Change modal title depending on whether it's in edit mode or create mode */}
          <Modal.Title>
            {supplierObj
              ? `Edit Supplier ${supplierObj.name || `#${supplierObj.id}`}`
              : 'Create Supplier'}
          </Modal.Title>
        </Modal.Header>

        {/* Show loading spinner when editing and data is being fetched */}
        {supplierObj && isEditButtonLoading ? (
          <Modal.Body
            className="procure-modal__body d-flex justify-content-center align-items-center"
            style={{ minHeight: '200px' }}
          >
            <div className="text-center">
              <Spinner animation="border" size="lg" className="mb-3" />
              <div className="text-muted">Loading supplier details...</div>
            </div>
          </Modal.Body>
        ) : (
          /* Formik component for handling form */
          <Formik
            initialTouched={
              supplierObj
                ? {
                    name: true,
                    websiteUrl: true,
                    email: true,
                    phonePrefix: true,
                    phoneSuffix: true,
                    relatedManufacturers: true,
                    secondaryEmails: secondaryEmails.map(() => true),
                  }
                : {
                    secondaryEmails: secondaryEmails.map(() => false),
                  }
            }
            initialValues={{
              name: supplierObj ? supplierObj?.name : name,
              websiteUrl: supplierObj?.website || '',
              email: supplierObj?.email || '',
              phonePrefix: supplierObj?.phone_prefix || '',
              phoneSuffix: supplierObj?.phone_suffix || '',
              innerSupplierId:
                supplierObj?.inner_supplier_id || innerSupplierId || '',
              includeInFinanceDigest: Boolean(
                supplierObj?.include_in_finance_digest
              ),
              secondaryEmails: secondaryEmails,
            }}
            enableReinitialize={true}
            validateOnMount={true}
            validateOnChange={!isSubmitting}
            validateOnBlur={!isSubmitting}
            validationSchema={formSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {/* Form fields and submit buttons are here */}
            {({
              handleChange,
              handleSubmit,
              values,
              handleBlur,
              touched,
              errors,
              isValid,
              setFieldTouched,
              setFieldValue,
              dirty,
            }) => {
              return (
                <Form noValidate onSubmit={handleSubmit}>
                  <Modal.Body className="procure-modal__body d-flex flex-column">
                    <Form.Group
                      controlId="formSupplierName"
                      className="field-margin"
                    >
                      <Form.Label>
                        Supplier Name {<RequiredAsteriskComponent />}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={event => {
                          const { value } = event.target;
                          handleChange(event);
                          setIsCheckingSupplierName(true);
                          setName(value);
                        }}
                        onBlur={handleBlur}
                        isInvalid={
                          (touched.name && !!errors.name) ||
                          (!supplierNameUniqueValidator.validate() && name)
                        }
                        isValid={
                          !errors.name &&
                          supplierNameUniqueValidator.validate() &&
                          name &&
                          !isCheckingSupplierName &&
                          isSupplierNameUnique
                        }
                      />

                      {(errors.name ||
                        (!supplierNameUniqueValidator.validate() &&
                          name &&
                          !isCheckingSupplierName)) && (
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                          {!supplierNameUniqueValidator.validate() &&
                            name &&
                            !isCheckingSupplierName &&
                            supplierNameUniqueValidator.text}
                        </Form.Control.Feedback>
                      )}
                      {isCheckingSupplierName && (
                        <Form.Text className="text-muted">
                          Checking...
                        </Form.Text>
                      )}
                    </Form.Group>
                    <Form.Group
                      controlId="formWebsiteUrl"
                      className="field-margin"
                    >
                      <Form.Label>Website URL</Form.Label>
                      <Form.Control
                        type="text"
                        name="websiteUrl"
                        value={values.websiteUrl}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isValid={!errors?.websiteUrl && values?.websiteUrl}
                        isInvalid={errors?.websiteUrl && values?.websiteUrl}
                      />

                      {errors.websiteUrl && (
                        <Form.Control.Feedback type="invalid">
                          {errors.websiteUrl}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                    <Form.Group
                      controlId="formInnerSupplierId"
                      className="field-margin"
                    >
                      <Form.Label>Inner Supplier ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="innerSupplierId"
                        value={values.innerSupplierId || ''}
                        onChange={event => {
                          handleChange(event);
                          setInnerSupplierId(event.target.value);
                        }}
                        onBlur={handleBlur}
                        isValid={
                          !errors?.innerSupplierId && values?.innerSupplierId
                        }
                        isInvalid={
                          errors?.innerSupplierId && values?.innerSupplierId
                        }
                        maxLength={100}
                      />
                      <Form.Text className="text-muted">
                        Optional internal identifier for associating quotes with
                        internal supplier records (max 100 characters).
                      </Form.Text>
                      {errors.innerSupplierId && (
                        <Form.Control.Feedback type="invalid">
                          {errors.innerSupplierId}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                    {/* Daily finance reception digest: backend default is excluded; unchecking does not clear pending digest rows. */}
                    <Form.Group
                      controlId="formIncludeInFinanceDigest"
                      className="field-margin"
                    >
                      <Form.Check
                        type="checkbox"
                        name="includeInFinanceDigest"
                        label="כלול בדוח קליטה יומי לכספים"
                        checked={values.includeInFinanceDigest}
                        onChange={e =>
                          setFieldValue(
                            'includeInFinanceDigest',
                            e.target.checked
                          )
                        }
                      />
                    </Form.Group>
                    <Form.Group
                      controlId="supplierEmail"
                      className="field-margin"
                    >
                      <Form.Label>Main Email</Form.Label>
                      <Form.Control
                        type="text"
                        name="email"
                        value={values.email}
                        onChange={event => {
                          const { value } = event.target;
                          handleChange(event);
                          setIsCheckingSupplierEmail(true);
                          setSupplierEmail(value);
                        }}
                        onBlur={handleBlur}
                        isInvalid={
                          (touched.email && !!errors.email) ||
                          (!supplierEmailUniqueValidator.validate() &&
                            supplierEmail)
                        }
                        isValid={
                          !errors.email &&
                          supplierEmailUniqueValidator.validate() &&
                          supplierEmail &&
                          !isCheckingSupplierEmail
                        }
                      />

                      {(errors.email ||
                        (!supplierEmailUniqueValidator.validate() &&
                          !isCheckingSupplierEmail &&
                          supplierEmail)) && (
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                          {!supplierEmailUniqueValidator.validate() &&
                            !isCheckingSupplierEmail &&
                            supplierEmail &&
                            supplierEmailUniqueValidator.text}
                        </Form.Control.Feedback>
                      )}
                      {isCheckingSupplierEmail && !errors.email && (
                        <Form.Text>Checking...</Form.Text>
                      )}
                    </Form.Group>
                    {/* If there are any secondary emails, render them via the SecondaryEmailComponent */}
                    {secondaryEmails.length > 0
                      ? secondaryEmails.map((email, index) => (
                          <SecondaryEmailComponent
                            key={index}
                            index={index}
                            handleDeleteEmail={removeSecondaryEmail}
                            onEmailChange={updateSecondaryEmail}
                            onSecondaryEmailValidation={setSecondaryEmailsValid}
                            supplierObjSecondaryEmail={
                              supplierObj?.secondary_emails?.[index]?.email
                            }
                            formik={{
                              handleChange,
                              values,
                              handleBlur,
                              touched,
                              errors,
                              setFieldTouched,
                              setFieldValue,
                            }}
                          />
                        ))
                      : null}
                    <Button
                      className="mb-2"
                      variant={'success'}
                      onClick={e => addSecondaryEmail(e)}
                    >
                      Add Email
                    </Button>
                    <Row className="mb-4">
                      <Form.Label>
                        Supplier Phone {<RequiredAsteriskComponent />}
                      </Form.Label>
                      <Form.Group
                        as={Col}
                        md="5"
                        controlId="supplierPhonePrefix"
                      >
                        <Form.Select
                          name="phonePrefix"
                          value={values.phonePrefix}
                          onChange={event => {
                            const { value } = event.target;
                            handleChange(event);
                            setIsCheckingSupplierPhone(true);
                            setPhonePrefix(value);
                          }}
                        >
                          <option value="" disabled>
                            - Select Prefix -
                          </option>
                          {PHONE_PREFIX_CHOICES.map((choice, index) => (
                            <option key={index} value={choice.value}>
                              {choice.label}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.phonePrefix && (
                          <Form.Control.Feedback type="invalid">
                            {errors.phonePrefix}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        md="7"
                        controlId="supplierPhoneSuffix"
                      >
                        <Form.Control
                          type="text"
                          name="phoneSuffix"
                          value={values.phoneSuffix}
                          onChange={event => {
                            const { value } = event.target;
                            handleChange(event);
                            setIsCheckingSupplierPhone(true);
                            setPhoneSuffix(value);
                          }}
                          onBlur={handleBlur}
                          isInvalid={
                            (touched.phoneSuffix && !!errors.phoneSuffix) ||
                            (!supplierPhoneUniqueValidator.validate() &&
                              phoneSuffix)
                          }
                          isValid={
                            !errors.phoneSuffix &&
                            supplierPhoneUniqueValidator.validate() &&
                            phoneSuffix &&
                            phoneSuffix.length === 7 &&
                            !isCheckingSupplierPhone &&
                            isSupplierPhoneUnique
                          }
                        />
                        {supplierPhoneUniqueValidator.validate() &&
                          phoneSuffix &&
                          !isCheckingSupplierPhone && (
                            <Form.Control.Feedback type="valid">
                              Looks good!
                            </Form.Control.Feedback>
                          )}
                        {(errors.phoneSuffix ||
                          (!supplierPhoneUniqueValidator.validate() &&
                            !isCheckingSupplierPhone &&
                            phoneSuffix)) && (
                          <Form.Control.Feedback type="invalid">
                            {errors.phoneSuffix}
                            {!supplierPhoneUniqueValidator.validate() &&
                              !isCheckingSupplierPhone &&
                              phoneSuffix &&
                              supplierPhoneUniqueValidator.text}
                          </Form.Control.Feedback>
                        )}
                        {isCheckingSupplierPhone && (
                          <Form.Text>Checking...</Form.Text>
                        )}
                      </Form.Group>
                    </Row>
                    {isLoadingManufacturers && manufacturerList.length === 0 ? (
                      <div className="d-flex align-items-center">
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        <span className="text-muted">
                          Loading manufacturers...
                        </span>
                      </div>
                    ) : (
                      <DropdownMultiselect
                        optionsList={manufacturerList}
                        selectedValues={relatedManufacturers}
                        setSelectedValues={setRelatedManufacturers}
                        placeholder="Manufacturers"
                      />
                    )}
                  </Modal.Body>
                  <Modal.Footer className="procure-modal__footer">
                    <Button
                      variant="primary"
                      className="glass-primary-btn"
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        (!supplierObj && !dirty) ||
                        !supplierNameUniqueValidator.validate() ||
                        !supplierEmailUniqueValidator.validate() ||
                        !supplierPhoneUniqueValidator.validate() ||
                        !secondaryEmailsValid ||
                        isCheckingSupplierName ||
                        isCheckingSupplierEmail ||
                        isCheckingSupplierPhone
                      }
                      onClick={handleSubmit}
                    >
                      {isSubmitting ? (
                        <Spinner
                          size="sm"
                          as="span"
                          animation="border"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : supplierObj ? (
                        'Save'
                      ) : (
                        'Create'
                      )}
                    </Button>

                    <Button
                      variant="secondary"
                      className="procure-modal__close-btn"
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal>
    </>
  );
};
export default SupplierModal;
